import { z } from 'zod';

/** Every change request carries a unique ID per tap, so retries can never run twice. */
export const IDEMPOTENCY_HEADER = 'idempotency-key';

export const COMMAND_ERROR_CODES = [
  'validation_failed',
  'unknown_command',
  'not_found',
  'version_conflict',
  'idempotency_conflict',
  'idempotency_key_required',
  'undo_refused',
] as const;
export type CommandErrorCode = (typeof COMMAND_ERROR_CODES)[number];

/** What the server answers after running a command. */
export const commandOutcomeSchema = z.object({
  ok: z.literal(true),
  commandId: z.string().uuid(),
  result: z.unknown(),
  /** Plain-language line, as shown in History ("Created note ..."). */
  summary: z.string(),
  undoable: z.boolean(),
  /** True when this exact request had already run and the stored answer was returned. */
  replayed: z.boolean(),
});
export type CommandOutcome = z.infer<typeof commandOutcomeSchema>;

export const commandRequestSchema = z.object({ input: z.unknown().optional() });

// ---- History ----

export const HISTORY_FILTERS = ['all', 'changes', 'security'] as const;
export type HistoryFilter = (typeof HISTORY_FILTERS)[number];

export const historyItemSchema = z.object({
  /** For changes: the command ID (used to undo it). */
  id: z.string(),
  kind: z.enum(['command', 'security']),
  at: z.iso.datetime(),
  /** "owner", "system", later "ai". */
  actor: z.string(),
  summary: z.string(),
  commandType: z.string().nullable(),
  undoable: z.boolean(),
  undone: z.boolean(),
  /** Coarse network of the device (security events only). */
  network: z.string().nullable(),
  device: z.string().nullable(),
});
export type HistoryItem = z.infer<typeof historyItemSchema>;

export const historyPageSchema = z.object({
  items: z.array(historyItemSchema),
  nextCursor: z.string().nullable(),
});
export type HistoryPage = z.infer<typeof historyPageSchema>;
